import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { Search, Loader2, Users, Building2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export function GlobalSearch() {
  const { isOfficeUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  const inputRef = useRef(null);

  // After navigating to any page, clear the search box so the next search starts fresh.
  useEffect(() => { setQ(""); setRes(null); setOpen(false); }, [location.pathname]);

  useEffect(() => {
    if (!q || q.trim().length < 2) { setRes(null); setLoading(false); return; }
    let active = true; setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(isOfficeUser ? "/agency/search" : "/search", { params: { q } });
        if (active) { setRes(data); setOpen(true); }
      } catch { if (active) setRes({ people: [], businesses: [] }); }
      finally { if (active) setLoading(false); }
    }, 300);
    return () => { active = false; clearTimeout(t); };
  }, [q, isOfficeUser]);

  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  useEffect(() => {
    const h = (e) => {
      const tag = document.activeElement?.tagName;
      if (e.key === "/" && tag !== "INPUT" && tag !== "TEXTAREA") { e.preventDefault(); inputRef.current?.focus(); }
    };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, []);

  const go = (path) => { setOpen(false); setQ(""); setRes(null); navigate(path); };
  const total = res ? (res.people?.length || 0) + (res.businesses?.length || 0) : 0;

  return (
    <div ref={boxRef} className="relative hidden lg:block min-w-[7rem] w-48 xl:w-60 2xl:w-72 shrink" data-testid="global-search-wrap">
      <div className="flex items-center gap-2 rounded-lg border border-border bg-secondary/60 px-3 py-2 w-full text-muted-foreground focus-within:ring-2 focus-within:ring-brand/20 focus-within:border-brand/40 transition">
        <Search className="w-4 h-4" />
        <input ref={inputRef} data-testid="global-search" value={q}
          onChange={(e) => setQ(e.target.value)} onFocus={() => res && setOpen(true)}
          placeholder="Search people, agencies…"
          className="bg-transparent text-sm outline-none w-full text-foreground" />
        {loading
          ? <Loader2 className="w-3.5 h-3.5 animate-spin" data-testid="global-search-spinner" />
          : <kbd className="hidden lg:inline text-[10px] text-muted-foreground border border-border rounded px-1">/</kbd>}
      </div>
      {open && q.trim().length >= 2 && (
        <div data-testid="global-search-results"
          className="absolute mt-1 w-80 max-h-96 overflow-y-auto tr-scroll rounded-lg border border-border bg-white shadow-lg z-50">
          {loading && <div className="px-3 py-3 text-sm text-slate-400" data-testid="global-search-loading">Searching…</div>}
          {!loading && total === 0 && (
            <div className="px-3 py-4 text-sm text-slate-400" data-testid="global-search-empty">
              No matching people or agencies found.
            </div>
          )}
          {!loading && res?.people?.length > 0 && (
            <div>
              <div className="px-3 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">People</div>
              {res.people.map((p) => (
                <button key={p.id} data-testid={`global-search-person-${p.id}`} onClick={() => go(`/people/detail/${p.id}`)}
                  className="w-full text-left px-3 py-2 hover:bg-secondary flex items-center gap-2">
                  <Users className="w-4 h-4 text-slate-400 shrink-0" />
                  <span className="min-w-0">
                    <span className="text-sm text-foreground truncate block">{p.name}</span>
                    {p.subtitle && <span className="text-xs text-slate-400 truncate block">{p.subtitle}</span>}
                  </span>
                </button>
              ))}
            </div>
          )}
          {!loading && res?.businesses?.length > 0 && (
            <div>
              <div className="px-3 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Agencies / Businesses</div>
              {res.businesses.map((b) => (
                <button key={b.id} data-testid={`global-search-business-${b.id}`} onClick={() => go(`/agencies/${b.id}`)}
                  className="w-full text-left px-3 py-2 hover:bg-secondary flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-slate-400 shrink-0" />
                  <span className="min-w-0">
                    <span className="text-sm text-foreground truncate block">{b.name}</span>
                    {b.subtitle && <span className="text-xs text-slate-400 truncate block">{b.subtitle}</span>}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
